<template>
  <div class="categories-container mx-lg-4 mx-1">
    <div class="categories-header">
      <h2 class="page-title">Categorías</h2>
      <button class="btn btn-create" @click="showCategoryModal = true">
        <i class="fas fa-plus me-2"></i>Crear Categoría
      </button>
    </div>    <div class="categories-content">
      <!-- Loading state -->
      <div v-if="isLoading" class="d-flex justify-content-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      
      <!-- Category List with Subcategories -->
      <div v-else class="table-responsive">
        <table class="table categories-table">
          <thead>
            <tr>
              <th>Categorías y Subcategorías</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="categories.length === 0">
              <td class="text-center py-4">
                <p class="text-muted mb-0">No hay categorías disponibles</p>
              </td>
            </tr>
            <template v-for="category in categories" :key="category.id">
              <!-- Category Row -->
              <tr class="category-row">
                <td colspan="2">
                  <div class="category-content">
                    <div class="category-info">
                      <div class="d-flex align-items-center mb-1">
                        <span class="category-name fw-bold">{{ category.name }}</span>
                        <span class="badge bg-primary ms-2">{{ category.product_count || 0 }}</span>
                      </div>
                      <small class="text-muted d-block">{{ category.slug }}</small>
                    </div>
                    <div class="category-actions mt-2">
                      <button class="btn btn-sm btn-action btn-action-edit me-2" @click="editCategory(category)" title="Editar categoría">
                        <i class="fas fa-edit me-1"></i>Editar
                      </button>
                      <button class="btn btn-sm btn-action btn-action-add me-2" @click="openSubcategoryModal(category)" title="Agregar subcategoría">
                        <i class="fas fa-plus me-1"></i>Subcategoría
                      </button>
                      <button class="btn btn-sm btn-action btn-action-delete" @click="deleteCategory(category.id)" title="Eliminar categoría">
                        <i class="fas fa-trash me-1"></i>Eliminar
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
              <!-- Subcategories -->
              <template v-if="category.children && category.children.length > 0">
                <tr class="subcategory-row" v-for="subcategory in category.children" :key="`${category.id}-${subcategory.id}`">
                  <td colspan="2">
                    <div class="subcategory-content ps-3">
                      <div class="subcategory-info">
                        <div class="d-flex align-items-center mb-1">
                          <i class="fas fa-level-down-alt me-2 text-muted fa-rotate-90"></i>
                          <span>{{ subcategory.name }}</span>
                          <span class="badge border-1 border border-primary text-primary ms-2">{{ subcategory.product_count || 0 }}</span>
                        </div>
                      </div>
                      <div class="subcategory-actions mt-2 ps-4">
                        <button class="btn btn-sm btn-action btn-action-edit me-2" @click="editSubcategory(subcategory, category)" title="Editar subcategoría">
                          <i class="fas fa-edit me-1"></i>Editar
                        </button>
                        <button class="btn btn-sm btn-action btn-action-delete" @click="deleteSubcategory(subcategory.id, category.id)" title="Eliminar subcategoría">
                          <i class="fas fa-trash me-1"></i>Eliminar
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
              
              <!-- Empty subcategories message -->
              <tr v-if="!category.children || category.children.length === 0" class="subcategory-empty">
                <td class="text-center text-muted py-2">
                  <small><i class="fas fa-info-circle me-1"></i> Sin subcategorías</small>
                </td>
              </tr>
              
              <!-- Spacer row -->
              <tr class="spacer-row">
                <td></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Category Modal -->
      <div class="modal" :class="{ 'show': showCategoryModal }" v-if="showCategoryModal">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ editingCategory ? 'Editar Categoría' : 'Nueva Categoría' }}</h5>
              <button class="btn-close" @click="closeCategoryModal"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="saveCategory">
                <div class="mb-3">
                  <label class="form-label">Nombre</label>
                  <input 
                    v-model="categoryForm.name" 
                    class="form-control" 
                    required
                  >
                </div>
                  <div class="mb-3">
                  <label class="form-label">Slug (generado automáticamente)</label>
                  <input 
                    v-if="editingCategory" 
                    v-model="categoryForm.slug" 
                    class="form-control" 
                    disabled
                  >
                  <input 
                    v-else
                    class="form-control" 
                    disabled
                    placeholder="Se generará automáticamente al guardar"
                  >
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button class="btn btn-cancel" @click="closeCategoryModal">Cancelar</button>
              <button class="btn btn-save" @click="saveCategory" :disabled="!categoryForm.name || saving">
                <i class="fas fa-spinner fa-spin me-2" v-if="saving"></i>
                {{ saving ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Add/Edit Subcategory Modal -->
      <div class="modal" :class="{ 'show': showAddSubcategoryModal }" v-if="showAddSubcategoryModal && selectedCategory">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ editingSubcategory ? 'Editar Subcategoría' : 'Nueva Subcategoría' }}</h5>
              <button class="btn-close" @click="closeAddSubcategoryModal"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="saveSubcategory">
                <div class="mb-3">
                  <label class="form-label">Categoría principal</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    :value="selectedCategory?.name" 
                    disabled
                  >
                </div>
                
                <div class="mb-3">
                  <label class="form-label">Nombre</label>
                  <input 
                    v-model="subcategoryForm.name" 
                    class="form-control" 
                    required
                  >
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button class="btn btn-cancel" @click="closeAddSubcategoryModal">Cancelar</button>
              <button class="btn btn-save" @click="saveSubcategory" :disabled="!subcategoryForm.name || saving">
                <i class="fas fa-spinner fa-spin me-2" v-if="saving"></i>
                {{ saving ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div class="modal" :class="{ 'show': showDeleteConfirmModal }" v-if="showDeleteConfirmModal">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Confirmar eliminación</h5>
              <button class="btn-close" @click="showDeleteConfirmModal = false"></button>
            </div>
            <div class="modal-body">
              <div class="alert alert-danger">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <span v-if="isDeleteSubcategory">
                  ¿Estás seguro de eliminar esta subcategoría?
                </span>
                <span v-else>
                  ¿Estás seguro de eliminar esta categoría? Esta acción también eliminará todas sus subcategorías.
                </span>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-cancel" @click="showDeleteConfirmModal = false">Cancelar</button>
              <button class="btn btn-confirm-delete" @click="confirmDelete" :disabled="saving">
                <i class="fas fa-spinner fa-spin me-2" v-if="saving"></i>
                {{ saving ? 'Eliminando...' : 'Eliminar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { getCategoriesHierarchical } from '../../services/categories';
import apiClient from '../../services/apiClient';

const toast = useToast();

// State variables
const categories = ref([]);
const isLoading = ref(false);

// UI state
const showCategoryModal = ref(false);
const showAddSubcategoryModal = ref(false);
const showDeleteConfirmModal = ref(false);
const saving = ref(false);
const selectedCategory = ref(null);
const editingCategory = ref(null);
const editingSubcategory = ref(null);
const deleteTarget = ref(null); // Will store the item to delete
const isDeleteSubcategory = ref(false); // Whether we're deleting a category or subcategory

// Form data - based on backend model
const categoryForm = ref({
  name: '',
  slug: ''
});

const subcategoryForm = ref({
  name: '',
  product_count: 0
});

// Methods
const fetchCategories = async () => {
  isLoading.value = true;
  try {
    // Use the hierarchical endpoint that already has parent-child relationships built
    const hierarchicalData = await getCategoriesHierarchical();
    console.log('Fetched hierarchical data:', hierarchicalData);
    
    // Ensure we have the expected structure
    if (hierarchicalData && Array.isArray(hierarchicalData)) {
      categories.value = hierarchicalData;
    } else {
      console.error('Unexpected data structure:', hierarchicalData);
      toast.error('Formato de datos inesperado');
    }
  } catch (error) {
    console.error('Error fetching categories:', error);
    toast.error('Error al cargar las categorías');
  } finally {
    isLoading.value = false;
  }
};

// Call fetchCategories on component mount
onMounted(() => {
  fetchCategories();
});

const closeCategoryModal = () => {
  showCategoryModal.value = false;
  editingCategory.value = null;
  categoryForm.value = { name: '', slug: '' };
};

const openSubcategoryModal = (category) => {
  selectedCategory.value = category;
  editingSubcategory.value = null;
  subcategoryForm.value = { name: '' };
  showAddSubcategoryModal.value = true;
};

const closeAddSubcategoryModal = () => {
  showAddSubcategoryModal.value = false;
  selectedCategory.value = null;
  editingSubcategory.value = null;
  subcategoryForm.value = { name: '', product_count: 0 };
};

const editCategory = (category) => {
  editingCategory.value = category;
  categoryForm.value = { ...category };
  showCategoryModal.value = true;
};

const editSubcategory = (subcategory, category) => {
  selectedCategory.value = category;
  editingSubcategory.value = subcategory;
  subcategoryForm.value = { 
    name: subcategory.name,
    slug: subcategory.slug,
    id: subcategory.id
  };
  showAddSubcategoryModal.value = true;
};

const saveCategory = async () => {
  if (!categoryForm.value.name) return;

  try {
    saving.value = true;
    
    if (editingCategory.value) {      // Update existing category
      console.log('Updating category:', editingCategory.value.id, categoryForm.value);
      await apiClient.put(`/categories/${editingCategory.value.id}/`, {
        name: categoryForm.value.name,
        parent: null // Main categories have no parent
        // Note: slug is automatically generated on the backend
      });
      toast.success('Categoría actualizada correctamente');
    } else {      // Add new category
      console.log('Creating new category:', categoryForm.value);
      await apiClient.post('/categories/', {
        name: categoryForm.value.name,
        parent: null // Main categories have no parent
        // Note: slug is automatically generated on the backend
        // children property is added by the hierarchical endpoint, not needed here
      });
      toast.success('Categoría creada correctamente');
    }
    
    // Refresh categories from server
    await fetchCategories();
    closeCategoryModal();  } catch (error) {
    console.error('Error saving category:', error);
    console.error('Error response:', error.response?.data);
    // Display more detailed error information
    let errorMsg = 'Error al guardar la categoría: ';
    if (error.response?.data) {
      // Django REST Framework might return errors in different formats
      if (error.response.data.detail) {
        errorMsg += error.response.data.detail;
      } else if (typeof error.response.data === 'object') {
        // Format field errors
        const fieldErrors = Object.entries(error.response.data)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join('; ');
        errorMsg += fieldErrors;
      } else {
        errorMsg += JSON.stringify(error.response.data);
      }
    } else {
      errorMsg += error.message || 'Error desconocido';
    }
    toast.error(errorMsg);
  } finally {
    saving.value = false;
  }
};

const saveSubcategory = async () => {
  if (!subcategoryForm.value.name || !selectedCategory.value) return;

  try {
    saving.value = true;
    
    if (editingSubcategory.value) {
      // Update existing subcategory
      await apiClient.put(`/categories/${editingSubcategory.value.id}/`, {
        name: subcategoryForm.value.name,
        parent: selectedCategory.value.id
      });
      toast.success('Subcategoría actualizada correctamente');
    } else {      // Add new subcategory
      console.log('Creating subcategory with parent ID:', selectedCategory.value.id);
      await apiClient.post('/categories/', {
        name: subcategoryForm.value.name,
        parent: selectedCategory.value.id
      });
      toast.success('Subcategoría creada correctamente');
    }
    
    // Refresh categories from server
    await fetchCategories();
    closeAddSubcategoryModal();
  } catch (error) {
    toast.error('Error al guardar la subcategoría: ' + (error.response?.data?.detail || error.message));
    console.error(error);
  } finally {
    saving.value = false;
  }
};

const closeDeleteConfirmModal = () => {
  showDeleteConfirmModal.value = false;
  deleteTarget.value = null;
  isDeleteSubcategory.value = false;
};

const deleteCategory = async (id) => {
  deleteTarget.value = id;
  isDeleteSubcategory.value = false;
  showDeleteConfirmModal.value = true;
};

const deleteSubcategory = async (id) => {
  deleteTarget.value = id;
  isDeleteSubcategory.value = true;
  showDeleteConfirmModal.value = true;
};

const confirmDelete = async () => {
  if (!deleteTarget.value) return;
  
  try {
    saving.value = true;
    console.log(`Deleting category ID: ${deleteTarget.value}`);
    
    // Send DELETE request
    const response = await apiClient.delete(`/categories/${deleteTarget.value}/`);
    console.log('Delete response:', response);
    
    // Refresh categories from server
    await fetchCategories();
    
    // Show success message
    if (isDeleteSubcategory.value) {
      toast.success('Subcategoría eliminada correctamente');
    } else {
      toast.success('Categoría eliminada correctamente');
    }
    
    // Close modal and reset
    closeDeleteConfirmModal();
  } catch (error) {
    console.error('Error during deletion:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status code:', error.response?.status);
    
    // Format error message
    let errorMsg = isDeleteSubcategory.value ? 
      'Error al eliminar la subcategoría: ' : 
      'Error al eliminar la categoría: ';
    
    if (error.response?.data) {
      if (error.response.data.detail) {
        errorMsg += error.response.data.detail;
      } else if (typeof error.response.data === 'object') {
        // Format structured error messages
        const formattedErrors = Object.entries(error.response.data)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join('; ');
        errorMsg += formattedErrors || JSON.stringify(error.response.data);
      } else {
        errorMsg += JSON.stringify(error.response.data);
      }
    } else {
      errorMsg += error.message || 'Error desconocido';
    }
    
    toast.error(errorMsg);
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
/* ===== Container ===== */
.categories-container {
  background: rgba(0,0,0,0.15);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  padding: 1.5rem;
  width: 100%;
  margin: 0 auto;
}

/* ===== Header ===== */
.categories-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #ccc;
  margin: 0;
  letter-spacing: -0.01em;
}

.btn-create {
  background: var(--primary-color);
  border: none;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-create:hover { background: var(--primary-dark); color: #fff; }

/* ===== Loading ===== */
.spinner-border.text-primary { color: var(--primary-color) !important; }

/* ===== Table ===== */
.categories-table {
  border-collapse: separate;
  border-spacing: 0;
  color: #aaa;
}

.categories-table thead th {
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  color: #666;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.65rem 0.75rem;
}

.table td { vertical-align: middle; }

/* ===== Category Rows ===== */
.category-row td {
  background: rgba(0,0,0,0.2);
  border-top: 1px solid rgba(255,255,255,0.04);
  border-bottom: 1px solid rgba(255,255,255,0.04);
  padding: 0;
}

.category-content, .subcategory-content { padding: 0.85rem; }

.category-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #ccc;
}

.category-info .badge.bg-primary {
  background: rgba(var(--primary-color-rgb), 0.2) !important;
  color: var(--primary-color);
  font-weight: 600;
  font-size: 0.68rem;
}

.category-info .text-muted { color: #555 !important; font-size: 0.78rem; }

/* ===== Action Buttons ===== */
.category-actions, .subcategory-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.btn-action {
  font-size: 0.78rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.btn-action-edit {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
}
.btn-action-edit:hover { color: #ccc; border-color: rgba(255,255,255,0.15); background: rgba(255,255,255,0.04); }

.btn-action-add {
  background: rgba(var(--primary-color-rgb), 0.1);
  border: 1px solid rgba(var(--primary-color-rgb), 0.25);
  color: var(--primary-color);
}
.btn-action-add:hover { background: rgba(var(--primary-color-rgb), 0.18); border-color: rgba(var(--primary-color-rgb), 0.35); }

.btn-action-delete {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(248,113,113,0.2);
  color: #f87171;
}
.btn-action-delete:hover { background: rgba(248,113,113,0.08); border-color: rgba(248,113,113,0.3); }

/* ===== Subcategory Rows ===== */
.subcategory-row td {
  border-bottom: 1px solid rgba(255,255,255,0.03);
  padding: 0;
}

.subcategory-content {
  background: rgba(255,255,255,0.015);
  border-left: 2px solid var(--primary-color);
}

.subcategory-content span { color: #aaa; font-size: 0.87rem; }

.subcategory-content .badge.border-primary {
  border-color: rgba(var(--primary-color-rgb), 0.35) !important;
  color: var(--primary-color) !important;
  font-size: 0.68rem;
}

.subcategory-content .text-muted { color: #444 !important; }

.subcategory-empty td {
  padding: 0.4rem;
  background: rgba(0,0,0,0.12);
  opacity: 0.6;
}
.subcategory-empty small { color: #444; font-size: 0.75rem; }

.spacer-row td {
  height: 6px;
  padding: 0;
  border: none;
  background: transparent;
}

/* ===== Modals ===== */
.modal {
  display: none;
  background: rgba(0,0,0,0.6);
  position: fixed;
  inset: 0;
  z-index: 1050;
}
.modal.show { display: flex; align-items: center; justify-content: center; }

.modal-dialog { margin: 0; max-width: 500px; width: 90%; }

.modal-content {
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  color: #aaa;
  border-radius: 10px;
  animation: modal-appear 0.2s ease-out;
}

.modal-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.modal-title { color: #ccc; font-size: 0.95rem; font-weight: 600; }

.modal-body { padding: 1.25rem; }
.modal-body p { color: #aaa; font-size: 0.84rem; }

.modal-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(255,255,255,0.06);
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.btn-close { filter: invert(0.5); }
.btn-close:hover { filter: invert(0.8); }

@keyframes modal-appear {
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Modal Buttons */
.btn-cancel {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  border-radius: 8px;
  font-size: 0.84rem;
  padding: 0.45rem 0.85rem;
  transition: all 0.15s ease;
}
.btn-cancel:hover { color: #ccc; border-color: rgba(255,255,255,0.15); }

.btn-save {
  background: var(--primary-color);
  border: none;
  color: #fff;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 0.45rem 0.85rem;
  transition: all 0.15s ease;
}
.btn-save:hover:not(:disabled) { background: var(--primary-dark); }
.btn-save:disabled { opacity: 0.5; }

.btn-confirm-delete {
  background: rgba(248,113,113,0.2);
  border: 1px solid rgba(248,113,113,0.3);
  color: #f87171;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 0.45rem 0.85rem;
  transition: all 0.15s ease;
}
.btn-confirm-delete:hover:not(:disabled) { background: rgba(248,113,113,0.3); }
.btn-confirm-delete:disabled { opacity: 0.5; }

/* ===== Forms ===== */
.form-label { color: #888; font-size: 0.82rem; font-weight: 600; }

.form-control, .form-select {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  font-size: 0.84rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.form-control::placeholder { color: #444; }
.form-control:focus, .form-select:focus {
  background: rgba(0,0,0,0.35);
  border-color: rgba(var(--primary-color-rgb), 0.4);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.15);
  color: #ddd;
}
.form-control:disabled {
  background: rgba(0,0,0,0.15);
  color: #555;
  opacity: 0.7;
}

/* ===== Alert ===== */
.alert-danger {
  background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.15);
  color: #f87171;
  border-radius: 8px;
  font-size: 0.84rem;
}

/* ===== Responsive ===== */
@media (max-width: 991px) {
  .categories-container { padding: 1rem; border-radius: 10px; }
  .categories-header { flex-direction: column; align-items: flex-start; gap: 0.75rem; }
  .page-title { font-size: 1.1rem; }
  .categories-header .btn-create { width: 100%; text-align: center; }
}

@media (max-width: 767px) {
  .categories-container { padding: 0.85rem; margin-left: 10px !important; margin-right: 10px !important; }
  .category-content, .subcategory-content { padding: 0.75rem; }
  .category-actions, .subcategory-actions { flex-wrap: wrap; }
  .btn-action { font-size: 0.76rem; padding: 0.28rem 0.55rem; }
  .modal-dialog { width: 95%; max-width: none; }
}

@media (max-width: 575px) {
  .categories-container { padding: 0.75rem; border-radius: 8px; }
  .category-content, .subcategory-content { padding: 0.65rem; }
  .category-actions, .subcategory-actions { gap: 0.35rem; margin-top: 0.5rem; }
  .btn-action { font-size: 0.74rem; padding: 0.26rem 0.5rem; }
  .category-name { font-size: 0.9rem; }
  .modal-dialog { width: calc(100% - 1rem); }
  .modal-header, .modal-body, .modal-footer { padding: 0.85rem; }
  .subcategory-content { padding-left: 0.5rem; }
}

@media (max-width: 399px) {
  .categories-container { padding: 0.6rem; }
  .category-content, .subcategory-content { padding: 0.55rem; }
  .category-actions, .subcategory-actions { gap: 0.25rem; }
  .btn-action { font-size: 0.72rem; padding: 0.25rem 0.45rem; }
  .category-name { font-size: 0.87rem; }
  .modal-header, .modal-body, .modal-footer { padding: 0.75rem; }
}

@media (max-width: 350px) {
  .category-actions, .subcategory-actions { flex-direction: column; align-items: stretch; }
  .btn-action { width: 100%; text-align: center; }
  .subcategory-actions { padding-left: 0 !important; }
}
</style>
